'use client';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';

import { ColorSchemeScript, MantineProvider } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import {
  AuthenticatorProvider,
  LogRocketProvider,
  StoreProvider,
} from '@scaleup/frontend/providers';

import { AlertProvider } from '@scaleup/frontend/ui';

import { SupplierIntakeWizard } from '@/components/intake';
import { CookieConsent } from '@/components/cookie-consent';
import { TermsWizard } from '@/components/terms-wizard';
import { routes } from '@/lib';
import '@scaleup/frontend/css/globals.css';
import { Inter } from 'next/font/google';
import { Suspense, useEffect } from 'react';

const inter = Inter({ subsets: ['latin'] });

const RootLayout = ({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) => {
  const unathenticatedPaths = [
    routes.onboarding.champion,
    routes.landing.root,
    routes.landing.suppliers,
    routes.landing.pricing,
    routes.landing.enterprises,
    routes.landing.about,
    routes.landing.supplierRegistration,
    routes.registration.supplier,
    routes.privacyPolicy
  ];

  useEffect(() => {
    const handler = (event: ErrorEvent) => {
      console.error('Global error caught:', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error
      });
    };
    
    window.addEventListener('error', handler);
    return () => window.removeEventListener('error', handler);
  }, []);
  
  return (
    <MantineProvider>
      <AlertProvider>
        <StoreProvider>
          <LogRocketProvider>
            <Suspense fallback={null}>
            </Suspense>
            <main>
              <div className="px-16 py-8 min-h-screen">
                <AuthenticatorProvider
                  unathenticatedPaths={unathenticatedPaths}
                >
                  {children}
                </AuthenticatorProvider>
              </div>
            </main>
          </LogRocketProvider>
        </StoreProvider>
      </AlertProvider>
    </MantineProvider>
  );
};

const ExportedLayout = ({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) => {
  return (
    <html lang="en">
      <head>
        <ColorSchemeScript />
      </head>
      <body className={`${inter.className} bg-primary-25`}>
        <RootLayout>
          <DatesProvider settings={{ consistentWeeks: true }}>
            <SupplierIntakeWizard />
            <TermsWizard />
            <CookieConsent />
            {children}
          </DatesProvider>
        </RootLayout>
      </body>
    </html>
  );
};

export default ExportedLayout;
